/**
 * The names each topic type defines for its sharing forms.
 *
 * A topic type can define names of its own in its `<type>/definitions.yaml`,
 * each entry a `define` statement like a form's: a news topic defines
 * `_headline` as `_title`. The expression can be any a `{…}` token holds, a
 * name in the simplest case or a call such as `uppercase(_title)`. A sharing
 * session pushes them over its share environment
 * (`EnvironmentLogic.topicTypeEnvironment`), run by the same
 * `StatementLogic.run` as a form's defines, so they are in scope only while a
 * topic of that type is being shared, and go with the environment when the
 * session ends. A define of `_shout` as `uppercase` binds the procedure,
 * since that is what `uppercase` evaluates to, so `{_shout(_title)}` is an
 * ordinary call.
 *
 * The files are globbed rather than listed, so a new type's file is picked up
 * by existing, and checked against `DEFINITIONS_SCHEMA` here; whether each
 * define runs as written is checked by `topic_type_def.test.ts`. Adding a
 * definition = adding one entry to its type's file. Deep-import this module
 * (not the package barrel): the form language loads it, and the barrel pulls
 * in the whole per-type component registry.
 */

import yaml from 'js-yaml';
import type { Define } from '@/modules/form/lang/statement';
import type { TopicTypeId } from '@/modules/topic/types';
import { DEFINITIONS_SCHEMA } from './schema';

const definitionYamls = import.meta.glob<string>('./*/definitions.yaml', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/** `source`, a `definitions.yaml`, as the define statements it holds. */
function parse(source: string, where: string): readonly Define[] {
  const parsed = DEFINITIONS_SCHEMA.safeParse(yaml.load(source));
  if (!parsed.success) {
    throw new Error(`${where}: ${parsed.error.message}`);
  }
  return parsed.data.map(
    ({ define, expr, doc }): Define => ({
      kind: 'define',
      name: define,
      expr,
      ...(doc === undefined ? {} : { doc }),
    })
  );
}

/**
 * Every topic type's definitions, parsed from its `definitions.yaml`. A file
 * that fails `DEFINITIONS_SCHEMA` throws here, naming its type, and
 * `topic_type_def.test.ts` loads every file so that happens in CI.
 */
const DEFS_BY_TOPIC_TYPE: Readonly<Record<string, readonly Define[]>> =
  Object.fromEntries(
    Object.entries(definitionYamls).flatMap(([path, source]) => {
      const match = path.match(/^\.\/([a-z]+)\/definitions\.yaml$/);
      return match ? [[match[1], parse(source, path)]] : [];
    })
  );

/** Every topic type's definitions, each name once, the first type's winning it. */
const ALL_TOPIC_TYPE_DEFS: readonly Define[] = Object.values(DEFS_BY_TOPIC_TYPE)
  .flat()
  .filter((d, i, all) => all.findIndex(e => e.name === d.name) === i);

/**
 * The defines a topic of `type` runs, in order; none for a type with no
 * file. With no type, every type's.
 */
function definitionsOf(type?: TopicTypeId): readonly Define[] {
  return type === undefined
    ? ALL_TOPIC_TYPE_DEFS
    : (DEFS_BY_TOPIC_TYPE[type] ?? []);
}

export const TopicTypeDefLogic = { definitionsOf };
